import React from 'react';
import { observer, inject } from 'mobx-react';
import qs from 'qs';
import Pagination from 'components/Pagination';
import Footer from 'components/Footer';
import * as DataList from 'components/DataList';

// A client's equipment (client page, Equipment tab), laid out like the
// Equipment list (components/Tables/Equipments) minus the client column.
const COLUMNS = [
  { area: 'equipment', label: 'Equipment', width: 'minmax(200px, 1.4fr)', bone: 'person' },
  { area: 'model', label: 'Make / model', width: 'minmax(130px, 1fr)' },
  { area: 'where', label: 'Equipment location', width: 'minmax(160px, 1.2fr)', bone: 'single', icon: 'map-marker' },
  { area: 'description', label: 'Description', width: 'minmax(180px, 1.4fr)', bone: 'single', icon: 'align-left' },
  { area: 'actions', label: '', width: '64px', bone: 'none' },
];

const CARDS = {
  columns: 'minmax(0, 1fr) auto',
  areas: [
    'equipment actions',
    'model model',
    'where where',
    'description description',
  ],
};

const MIN_TABLE_WIDTH = 760;

const thumbStyle = {
  flex: 'none',
  width: 40,
  height: 40,
  borderRadius: 8,
  objectFit: 'cover',
  display: 'block',
};

@inject('viewStore', 'equipmentStore') @observer
class EquipmentsTable extends React.Component {

  componentDidMount() {
    this.load(1);
  }

  load(page) {
    const { viewStore, equipmentStore, client } = this.props;
    viewStore.clientEquipmentSearch = {
      ...viewStore.clientEquipmentSearch,
      where: { ...viewStore.clientEquipmentSearch.where, client_id: client.attributes.id },
      page,
    };
    equipmentStore.loadClientEquipments();
  }

  showLink(equipment) {
    const { viewStore } = this.props;
    return `/dashboard/equipments/${equipment.id}/show?${qs.stringify(viewStore.equipmentShowSearch, { encode: false })}`;
  }

  render() {
    const {
      viewStore,
      equipmentStore: {
        clientEquipments: { objects, isLoading, isError, meta },
      },
    } = this.props;

    if (isLoading && objects.length === 0) {
      return (
        <DataList.Loading columns={COLUMNS} cards={CARDS} minTableWidth={MIN_TABLE_WIDTH} noun="equipment" rows={5} />
      );
    }
    if (isError) {
      return <DataList.LoadError noun="equipment" onRetry={() => this.load((meta && meta.page) || 1)} />;
    }
    if (objects.length === 0) {
      return (
        <DataList.Empty
          noun="equipment"
          message="Equipment you add for this client will show up here."
        />
      );
    }

    const pagination = meta && meta.total_pages > 1 && (
      <Footer>
        <div className="bp3-navbar-group bp3-align-left">
          <Pagination value={meta.page} total={meta.total_pages} onChange={(page) => this.load(page)} />
        </div>
      </Footer>
    );

    return (
      <React.Fragment>
        <DataList.List
          preferTable
          columns={COLUMNS}
          cards={CARDS}
          minTableWidth={MIN_TABLE_WIDTH}
          total={(meta && meta.total_records) || objects.length}
          noun="equipment"
          note={DataList.sortNote(viewStore.clientEquipmentSearch.order)}
        >
          {objects.map((equipment) => {
            const attributes = equipment.attributes;
            const image = attributes.images && attributes.images.length > 0 && attributes.images[0].asset && attributes.images[0].asset.thumb
              ? attributes.images[0].asset.thumb.url
              : '/../equipment_default.jpg';
            const name = attributes.kind || attributes.serial_number || 'Equipment';
            const serial = attributes.serial_number ? `SN ${attributes.serial_number}` : 'No serial number';
            const address = attributes.location && attributes.location.full_address;
            return (
              <DataList.Row key={equipment.id}>
                <DataList.Cell area="equipment">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                    <img src={image} alt="" style={thumbStyle} />
                    <DataList.Lines
                      primary={
                        <DataList.RowLink href={this.showLink(equipment)} label={`Equipment ${name}`}>
                          {name}
                        </DataList.RowLink>
                      }
                      secondary={attributes.kind ? serial : null}
                    />
                  </div>
                </DataList.Cell>
                <DataList.Cell area="model">
                  {attributes.make || attributes.eq_model ? (
                    <DataList.Lines primary={attributes.make || 'Unknown make'} secondary={attributes.eq_model} strong={false} />
                  ) : (
                    <DataList.Muted>No make or model</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="where">
                  {attributes.eq_location || address ? (
                    <DataList.Lines
                      primary={attributes.eq_location || <DataList.Muted>No location</DataList.Muted>}
                      secondary={address}
                      strong={false}
                    />
                  ) : (
                    <DataList.Muted>No location</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="description">
                  {attributes.description ? (
                    <DataList.Lines primary={attributes.description} strong={false} title={attributes.description} />
                  ) : (
                    <DataList.Muted>No description</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="actions">
                  <DataList.Actions
                    items={[
                      { icon: 'edit', label: 'Edit equipment', onClick: () => viewStore.equipmentShow({ uuid: attributes.id }) },
                    ]}
                  />
                </DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        {pagination}
      </React.Fragment>
    );
  }
}

export default EquipmentsTable;
